import React from "react";
import { Blocks } from "@/types/cms";
import { renderRichTextWithMutedStrikethrough } from "@/lib/richText";
import { resolveLucideIcon } from "@/lib/lucideIcon";
import styles from "./SectionHeader.module.css";

interface SectionHeaderProps {
  content?: Blocks;
  align?: 'left' | 'center' | 'right';
  textAlign?: 'left' | 'center' | 'right';
  headerWidth?: 'sm' | 'md' | 'lg';
  eyebrow?: string;
  icon?: string;
}

const SectionHeader = ({ content, align = 'center', textAlign, headerWidth, eyebrow, icon }: SectionHeaderProps) => {
  if (!content && !eyebrow) return null;

  const eyebrowIcon = resolveLucideIcon(icon);

  // For center alignment, keep text left but center the wrapper unless textAlign overrides
  const containerAlign = textAlign
    ? (textAlign === 'left' ? styles.alignLeft : textAlign === 'right' ? styles.alignRight : styles.alignCenter)
    : (align === 'right' ? styles.alignRight : styles.alignLeft);
  const wrapperJustify = align === 'left' ? styles.justifyStart : align === 'right' ? styles.justifyEnd : styles.justifyCenter;

  const widthClass = headerWidth === 'sm' ? styles.wSm : headerWidth === 'lg' ? styles.wLg : headerWidth === 'md' ? styles.wMd : '';

  // `reveal-item` at index 0 makes the header the first thing to move in a
  // staggered card section. Marking it here rather than at each call site
  // covers every CardSection layout branch (grid, the 2-up splits, showcase,
  // the carousels) from one place. Outside a `SectionReveal` the class is
  // inert — nothing hides it unless an ancestor carries a reveal state class.
  // See documentation/loading-animations.md.
  return (
    <div
      className={`reveal-item ${styles.container} ${containerAlign} ${widthClass}`}
      style={{ "--reveal-index": 0 } as React.CSSProperties}
    >
      <div className={`${styles.row} ${wrapperJustify}`}>
        <div className={styles.textWrap}>
          {eyebrow && (
            <div className={styles.eyebrowRow}>
              {eyebrowIcon &&
                React.createElement(eyebrowIcon, {
                  className: styles.eyebrowIcon,
                  strokeWidth: 1.5,
                })}
              <span className={`eyebrow ${styles.eyebrow}`}>{eyebrow}</span>
            </div>
          )}
          {content && (
            <div className={`${styles.full} ${styles.content}`}>
              {renderRichTextWithMutedStrikethrough(content, {
                defaultAlign: textAlign,
                headingVariant: "accent",
                paragraphClass: "body-lg",
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionHeader;


